import { BIBLE_BOOKS } from './bibleData';

const OT_BOOKS = BIBLE_BOOKS.slice(0, 39);
const NT_BOOKS = BIBLE_BOOKS.slice(39);
const OT_BOOK_IDS = new Set(OT_BOOKS.map(b => b.id));

export const BADGE_DEFS = [
  // Recording — verse milestones
  { type: 'first-recording',       name: 'First Voice',           description: 'Recorded your first Bible verse',                 category: 'recording' },
  { type: 'milestone-10',          name: 'Getting Started',        description: 'Recorded 10 Bible verses',                        category: 'recording' },
  { type: 'milestone-100',         name: 'Century Club',           description: 'Recorded 100 Bible verses',                       category: 'recording' },
  // Recording — streaks
  { type: 'recording-streak-3',    name: '3-Day Streak',           description: 'Recorded for 3 consecutive days',                 category: 'recording' },
  { type: 'recording-streak-7',    name: 'Week Warrior',           description: 'Recorded for 7 consecutive days',                 category: 'recording' },
  { type: 'recording-streak-30',   name: 'Month Master',           description: 'Recorded for 30 consecutive days',                category: 'recording' },
  // Recording — chapter milestones
  { type: 'old-testament-warrior', name: 'OT Warrior',             description: 'Recorded 20 chapters from the Old Testament',     category: 'recording' },
  { type: 'recording-bronze',      name: 'Recording Bronze',       description: 'Completed recording of your first whole plan',    category: 'recording' },
  { type: 'recording-silver',      name: 'Recording Silver',       description: 'Recorded 25 chapters of the Bible',               category: 'recording' },
  { type: 'recording-gold',        name: 'Recording Gold',         description: 'Recorded 50 chapters of the Bible',               category: 'recording' },
  { type: 'whole-book-club',        name: 'Whole Book Club',         description: 'Recorded your first complete book of the Bible',  category: 'recording' },
  { type: 'recording-librarian',   name: 'Recording Librarian',    description: 'Fully recorded 10 books of the Bible',            category: 'recording' },
  { type: 'studio-star',           name: 'Studio Star',            description: 'Recorded 100 chapters of the Bible',              category: 'recording' },
  { type: 'studio-master-nt',      name: 'Studio Master NT',       description: 'Completed the New Testament',                     category: 'recording' },
  { type: 'studio-master-ot',      name: 'Studio Master OT',       description: 'Completed the Old Testament',                     category: 'recording' },
  { type: 'family-founders',       name: 'Family Founders',        description: 'Completed the whole Bible',                       category: 'recording' },
  // Listening
  { type: 'listener-emmy',         name: 'Listener Emmy',          description: 'Listened to your first whole chapter',            category: 'listening' },
  { type: 'listener-bronze',       name: 'Listener Bronze',        description: 'Completed your first listening plan',             category: 'listening' },
  { type: 'listener-silver',       name: 'Listener Silver',        description: 'Listened to 25 chapters of the Bible',            category: 'listening' },
  { type: 'listener-gold',         name: 'Listener Gold',          description: 'Listened to 50 chapters of the Bible',            category: 'listening' },
];

export const getBadgeName = (type) => BADGE_DEFS.find(b => b.type === type)?.name || 'Achievement';
export const getBadgeDescription = (type) => BADGE_DEFS.find(b => b.type === type)?.description || 'Great achievement!';

// Fixed streak calc: counts today or yesterday as the anchor, not strictly today.
export function calculateDailyStreak(records) {
  if (!records || records.length === 0) return 0;

  const toStr = (raw) => {
    const d = raw?.toDate ? raw.toDate() : new Date(raw);
    return d.toISOString().slice(0, 10); // YYYY-MM-DD in local... use UTC to be consistent
  };

  const dateSet = new Set(records.map(r => toStr(r.createdAt)));

  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const yd = new Date(now);
  yd.setDate(yd.getDate() - 1);
  const yesterdayStr = yd.toISOString().slice(0, 10);

  if (!dateSet.has(todayStr) && !dateSet.has(yesterdayStr)) return 0;

  const check = new Date(now);
  if (!dateSet.has(todayStr)) check.setDate(check.getDate() - 1);

  let streak = 0;
  while (dateSet.has(check.toISOString().slice(0, 10))) {
    streak++;
    check.setDate(check.getDate() - 1);
  }
  return streak;
}

export async function checkAndAwardBadges(userId, db) {
  try {
    const {
      collection, getDocs, doc, getDoc, updateDoc, addDoc, serverTimestamp, query, where
    } = await import('firebase/firestore');

    const userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) return [];
    const userData = userSnap.data();
    const currentBadges = userData.badges || [];
    const earned = new Set(currentBadges.map(b => b.type));

    const newBadges = [];
    const award = (type, description) => {
      if (!earned.has(type)) {
        newBadges.push({ type, earnedAt: new Date(), description });
        earned.add(type);
      }
    };

    // ── Recording badges ──────────────────────────────────────────────────────
    const recSnap = await getDocs(
      query(collection(db, 'recordings'), where('userId', '==', userId))
    );
    const recordings = recSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    if (recordings.length >= 1)   award('first-recording',  'Recorded your first Bible verse');
    if (recordings.length >= 10)  award('milestone-10',      'Recorded 10 Bible verses');
    if (recordings.length >= 100) award('milestone-100',     'Recorded 100 Bible verses');

    const dailyStreak = calculateDailyStreak(recordings);
    if (dailyStreak >= 3)  award('recording-streak-3',  'Recorded for 3 consecutive days');
    if (dailyStreak >= 7)  award('recording-streak-7',  'Recorded for 7 consecutive days');
    if (dailyStreak >= 30) award('recording-streak-30', 'Recorded for 30 consecutive days');

    // Build unique-chapter and per-book maps
    const recordedByBook = {};
    const uniqueChapters = new Set();
    recordings.forEach(r => {
      if (!r.book || r.chapter == null) return;
      uniqueChapters.add(`${r.book}_${r.chapter}`);
      if (!recordedByBook[r.book]) recordedByBook[r.book] = new Set();
      recordedByBook[r.book].add(r.chapter);
    });

    let otChapterCount = 0;
    uniqueChapters.forEach(key => {
      if (OT_BOOK_IDS.has(key.split('_')[0])) otChapterCount++;
    });

    if (otChapterCount >= 20)      award('old-testament-warrior', 'Recorded 20 chapters from the Old Testament');
    if (uniqueChapters.size >= 25)  award('recording-silver',      'Recorded 25 chapters of the Bible');
    if (uniqueChapters.size >= 50)  award('recording-gold',        'Recorded 50 chapters of the Bible');
    if (uniqueChapters.size >= 100) award('studio-star',           'Recorded 100 chapters of the Bible');

    const isBookComplete = (bookId, totalChapters) => {
      const recorded = recordedByBook[bookId];
      if (!recorded) return false;
      for (let c = 1; c <= totalChapters; c++) {
        if (!recorded.has(c)) return false;
      }
      return true;
    };

    const completedBooks = BIBLE_BOOKS.filter(b => isBookComplete(b.id, b.chapters)).length;
    if (completedBooks >= 1)  award('whole-book-club',     'Recorded your first complete book of the Bible');
    if (completedBooks >= 10) award('recording-librarian', 'Fully recorded 10 books of the Bible');

    const ntDone = NT_BOOKS.every(b => isBookComplete(b.id, b.chapters));
    const otDone = OT_BOOKS.every(b => isBookComplete(b.id, b.chapters));
    if (ntDone) award('studio-master-nt', 'Completed the New Testament');
    if (otDone) award('studio-master-ot', 'Completed the Old Testament');
    if (ntDone && otDone) award('family-founders', 'Completed the whole Bible');

    // ── Plan badges ───────────────────────────────────────────────────────────
    const recPlansSnap = await getDocs(collection(db, `users/${userId}/recordingPlans`));
    const hasCompletedRecordingPlan = recPlansSnap.docs.some(d => {
      const s = d.data().status;
      return s === 'completed' || s === 'archived';
    });
    if (hasCompletedRecordingPlan) {
      award('recording-bronze', 'Completed recording of your first whole plan');
    }

    // ── Listening badges ──────────────────────────────────────────────────────
    const listenSnap = await getDocs(
      query(collection(db, 'listenEvents'), where('userId', '==', userId))
    );
    const listenedChapters = new Set();
    listenSnap.docs.forEach(d => {
      const data = d.data();
      if (data.book && data.chapter != null) listenedChapters.add(`${data.book}_${data.chapter}`);
    });

    if (listenedChapters.size >= 1)  award('listener-emmy',   'Listened to your first whole chapter');
    if (listenedChapters.size >= 25) award('listener-silver',  'Listened to 25 chapters of the Bible');
    if (listenedChapters.size >= 50) award('listener-gold',    'Listened to 50 chapters of the Bible');

    // Listening plan: check if all plan chapters have a listen event
    const listenPlansSnap = await getDocs(collection(db, `users/${userId}/listeningPlans`));
    let completedListeningPlan = listenPlansSnap.docs.some(d => d.data().status === 'completed');
    if (!completedListeningPlan) {
      for (const planDoc of listenPlansSnap.docs) {
        const planData = planDoc.data();
        if (!planData.planId) continue;
        const planSnap = await getDoc(doc(db, 'plans', planData.planId));
        if (!planSnap.exists()) continue;
        const chapters = planSnap.data().chapters || [];
        if (chapters.length > 0 && chapters.every(ch => listenedChapters.has(`${ch.book}_${ch.chapter}`))) {
          completedListeningPlan = true;
          await updateDoc(planDoc.ref, { status: 'completed', completedAt: serverTimestamp() });
          break;
        }
      }
    }
    if (completedListeningPlan) award('listener-bronze', 'Completed your first listening plan');

    // ── Persist new badges ────────────────────────────────────────────────────
    if (newBadges.length > 0) {
      await updateDoc(doc(db, 'users', userId), {
        badges: [...currentBadges, ...newBadges],
        updatedAt: serverTimestamp()
      });

      // Fire badge celebration for family notifications
      try {
        const familyIds = userData.familyIds || [];
        if (familyIds.length > 0) {
          await addDoc(collection(db, 'badgeCelebrations'), {
            userId,
            userEmail: userData.email,
            userName: userData.displayName || userData.firstName || userData.email,
            familyIds,
            badges: newBadges.map(b => ({ type: b.type, name: getBadgeName(b.type), description: b.description })),
            createdAt: serverTimestamp()
          });
        }
      } catch (_) {
        // Email notification failure is non-critical
      }

      console.log('🏆 Awarded badges:', newBadges.map(b => b.type));
    }

    return newBadges;
  } catch (error) {
    console.error('Error checking badges:', error);
    return [];
  }
}
